import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useJobsSlice } from 'app/pages/JobsPage/slice';
import {
  selectIsPanelOpen,
  selectSelectedTab,
  selectSearchQuery,
  selectIsPolling,
  selectLastFetchByTab,
  selectIsTabLoading,
  selectJobsForTab,
} from 'app/pages/JobsPage/slice/selectors';
import { getFilteredTabs } from '../config';

// Function to determine default tab based on current route
const getDefaultTabForRoute = (pathname: string) => {
  if (pathname.includes('/app/song-generator')) return 'song-generation';
  if (pathname.includes('/app/generate')) return 'inference-v2';
  if (pathname.includes('/app/text-to-song')) return 'lyrics-to-music';
  if (pathname.includes('/app/tts')) return 'tts';
  if (pathname.includes('/app/stem-splitter')) return 'stem-splitter';
  if (pathname.includes('/app/instruments')) return 'instrument-inference';
  // Add more route mappings as needed
  return 'inference-v2'; // fallback
};

export const useRecentJobsPanel = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { actions } = useJobsSlice();

  // Get default tab based on current route
  const defaultTab = getDefaultTabForRoute(location.pathname);

  // Get filtered tabs based on current route
  const filteredTabs = getFilteredTabs(location.pathname);

  // Selectors
  const isPanelOpen = useSelector(selectIsPanelOpen);
  const selectedTab = useSelector(selectSelectedTab);
  const searchQuery = useSelector(selectSearchQuery);
  const isPolling = useSelector(selectIsPolling);
  const lastFetchByTab = useSelector(selectLastFetchByTab);
  const jobs = useSelector(selectJobsForTab(selectedTab || defaultTab));
  const isLoading = useSelector(
    selectIsTabLoading(selectedTab || defaultTab),
  );

  // Get current tab config from filtered tabs
  const tabConfig = filteredTabs.find(tab => tab.key === selectedTab);

  // Actions
  const setPanelOpen = (open: boolean) => {
    dispatch(actions.setPanelOpen(open));
  };

  const setSelectedTab = (tab: string) => {
    dispatch(actions.setSelectedTab(tab));
  };

  const setSearchQuery = (query: string) => {
    dispatch(actions.setSearchQuery(query));
  };

  const setPolling = (polling: boolean) => {
    dispatch(actions.setPolling(polling));
  };

  const fetchJobs = async (Api: any, forceRefresh = false, showLoadingState = true) => {
    if (!tabConfig) return;

    // Check if we need to fetch (no jobs or force refresh or old data)
    const lastFetch = lastFetchByTab[selectedTab || defaultTab];
    const shouldFetch =
      forceRefresh ||
      !jobs?.length ||
      !lastFetch ||
      Date.now() - lastFetch > 5 * 60 * 1000; // 5 minutes

    if (!shouldFetch) return;

    if (showLoadingState) {
      dispatch(actions.setTabLoading({ tabKey: selectedTab || defaultTab, loading: true }));
    }
    try {
      const res = await tabConfig.fetchJobs(Api);
      dispatch(
        actions.updateJobsForTab({
          tabKey: selectedTab || defaultTab,
          jobs: res.jobs || [],
        }),
      );
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      if (showLoadingState) {
        dispatch(actions.setTabLoading({ tabKey: selectedTab || defaultTab, loading: false }));
      }
    }
  };

  const deleteJob = (jobId: string) => {
    dispatch(actions.deleteJobFromTab({ tabKey: selectedTab || defaultTab, jobId: jobId || '' }));
  };

  const clearJobsForTab = (tabKey: string) => {
    dispatch(actions.clearJobsForTab(tabKey));
  };

  const clearAllJobs = () => {
    dispatch(actions.clearAllJobs());
  };

  return {
    // State
    isPanelOpen,
    selectedTab,
    searchQuery,
    isPolling,
    jobs,
    isLoading,
    tabConfig,
    lastFetchByTab,
    defaultTab,
    filteredTabs,

    // Actions
    setPanelOpen,
    setSelectedTab,
    setSearchQuery,
    setPolling,
    fetchJobs,
    deleteJob,
    clearJobsForTab,
    clearAllJobs,
  };
};
